// Working on a tournament with somebody else: looking an arbiter up, inviting
// them, the notifications both sides read, leaving or being removed, deleting a
// tournament and telling everyone, and the live listener the spectator view and
// the co-arbiter sync both hang off.
//
// Moved here from firebase-auth.js unchanged. It imports cloud-sync.js (for the
// assembled tournament shape and to drop a deleted tournament's caches); the
// dependency runs one way only, so there is no cycle.
import {
    auth, db, storage,
    doc, setDoc, getDoc, collection, query, where, getDocs, deleteDoc,
    onSnapshot, updateDoc, arrayUnion, arrayRemove,
    storageRef, listAll, deleteObject
} from "./firebase-core.js";
import { currentUser } from "./session.js";
import { assembleTournament, forgetTournamentCaches, notePrivateHeld } from "./cloud-sync.js";
import { snapshotIsAuthoritative, offlineError } from "../utils/offline-guards.js";
import { FUNCTIONS_BASE } from "./backend.js";

// --- Cooperation & Notification Functions ---

// Server-side arbiter lookup (Cloud Function). Replaces the old full
// users-collection scan: the locked-down Firestore rules make user docs
// readable only by their owner, so cross-user email checks must go through
// the server. Returns { exists, uid, role } — never the whole user doc.
const LOOKUP_ARBITER_URL = `${FUNCTIONS_BASE}/lookupArbiter`;

async function lookupArbiterByEmail(emailLc) {
    const token = await auth.currentUser.getIdToken();
    const response = await fetch(`${LOOKUP_ARBITER_URL}?email=${encodeURIComponent(emailLc)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
        throw new Error("Could not verify this email address right now. Please try again.");
    }
    return await response.json();
}

async function inviteArbiterByEmail(tournamentId, tournamentName, email) {
    if (!currentUser) throw new Error("Must be logged in to invite arbiters.");
    const emailToInvite = email.trim().toLowerCase();
    if (emailToInvite === currentUser.email.toLowerCase()) {
        throw new Error("You cannot invite yourself to cooperate on your own tournament!");
    }

    // 1. Verify recipient user exists and has Arbiter role (server-side lookup)
    const lookup = await lookupArbiterByEmail(emailToInvite);
    if (!lookup.exists) {
        throw new Error("No registered account found with this email address.");
    }
    const recipientUid = lookup.uid;

    if (lookup.role === 'player') {
        throw new Error("This email is registered with a Player account. You can only invite users with an Arbiter account.");
    }

    // 2. Update tournament document to include pending email
    const tourneyRef = doc(db, "tournaments", tournamentId);
    
    // Read current data first to avoid replacing other array items
    const tourneySnap = await getDoc(tourneyRef);
    if (!tourneySnap.exists()) {
        throw new Error("Tournament document not found globally in Firestore yet. Make sure it is saved.");
    }

    const currentTourneyData = tourneySnap.data();

    // Only the tournament owner may invite cooperating arbiters.
    if (currentTourneyData.ownerId && currentTourneyData.ownerId !== currentUser.uid) {
        throw new Error("Only the tournament owner can invite cooperating arbiters.");
    }

    const cooperating = currentTourneyData.cooperatingArbiters || [];
    const pending = currentTourneyData.pendingCooperatingArbiters || [];

    if (cooperating.includes(emailToInvite)) {
        throw new Error("This user is already a cooperating arbiter on this tournament.");
    }
    if (pending.includes(emailToInvite)) {
        throw new Error("This user has already been invited and the invitation is pending.");
    }

    await updateDoc(tourneyRef, {
        pendingCooperatingArbiters: arrayUnion(emailToInvite)
    });

    // 3. Create a pending notification
    const notiId = Date.now().toString() + "_" + Math.random().toString(36).substring(2, 7);
    await setDoc(doc(db, "notifications", notiId), {
        recipientEmail: emailToInvite,
        senderUid: currentUser.uid,
        senderEmail: currentUser.email,
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        tournamentId: tournamentId,
        tournamentName: tournamentName,
        type: 'cooperation_invite',
        status: 'pending',
        timestamp: new Date().toISOString()
    });

    return { email: emailToInvite, uid: recipientUid };
}

function listenToNotifications(email, callback) {
    if (!currentUser) return () => {};
    const q = query(collection(db, "notifications"), where("recipientEmail", "==", email.trim().toLowerCase()));
    return onSnapshot(q, (snapshot) => {
        const list = [];
        snapshot.forEach(document => {
            list.push({ id: document.id, ...document.data() });
        });
        list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        callback(list);
    });
}

async function acceptCooperationInvite(notification) {
    if (!currentUser) return;

    // 1. Join the tournament FIRST. This is the step that can fail (the rules
    //    require us to still be in pendingCooperatingArbiters — an invite the
    //    owner has since retracted is refused), and the notification is the only
    //    way back into this flow. Deleting it first, as this used to, turned any
    //    failure here into an invitation that could never be accepted OR declined.
    const myEmailLc = (currentUser.email || '').trim().toLowerCase();
    const tourneyRef = doc(db, "tournaments", notification.tournamentId);
    await updateDoc(tourneyRef, {
        pendingCooperatingArbiters: arrayRemove(myEmailLc),
        cooperatingArbiters: arrayUnion(myEmailLc)
    });

    // 2. Clear the invite from the notifications list.
    const notiRef = doc(db, "notifications", notification.id);
    await deleteDoc(notiRef);

    // 3. Send notification back to sender
    const replyNotiId = Date.now().toString() + "_" + Math.random().toString(36).substring(2, 7);
    await setDoc(doc(db, "notifications", replyNotiId), {
        recipientEmail: (notification.senderEmail || '').trim().toLowerCase(),
        senderUid: currentUser.uid,
        senderEmail: currentUser.email,
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        tournamentId: notification.tournamentId,
        tournamentName: notification.tournamentName,
        type: 'invite_accepted',
        status: 'pending',
        timestamp: new Date().toISOString()
    });
}

async function declineCooperationInvite(notification) {
    if (!currentUser) return;

    // 1. Leave the pending list first, then clear the notification — same
    //    ordering rule as accept above: never destroy the way back into the flow
    //    before the step that can fail has actually succeeded.
    const tourneyRef = doc(db, "tournaments", notification.tournamentId);
    await updateDoc(tourneyRef, {
        pendingCooperatingArbiters: arrayRemove((currentUser.email || '').trim().toLowerCase())
    });

    // 2. Clear the invite from the notifications list.
    const notiRef = doc(db, "notifications", notification.id);
    await deleteDoc(notiRef);

    // 3. Send declination notification back to sender
    const replyNotiId = Date.now().toString() + "_" + Math.random().toString(36).substring(2, 7);
    await setDoc(doc(db, "notifications", replyNotiId), {
        recipientEmail: (notification.senderEmail || '').trim().toLowerCase(),
        senderUid: currentUser.uid,
        senderEmail: currentUser.email,
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        tournamentId: notification.tournamentId,
        tournamentName: notification.tournamentName,
        type: 'invite_declined',
        status: 'pending',
        timestamp: new Date().toISOString()
    });
}

async function deleteNotification(id) {
    await deleteDoc(doc(db, "notifications", id));
}

async function retractCooperationInvite(tournamentId, email) {
    if (!currentUser) throw new Error("Must be logged in to retract invitations.");
    const emailToRetract = (email || '').trim().toLowerCase();
    if (!emailToRetract) throw new Error("No email provided to retract.");

    // 1. Remove the email from the tournament's pending list (owner only)
    const tourneyRef = doc(db, "tournaments", tournamentId);
    const tourneySnap = await getDoc(tourneyRef);
    if (tourneySnap.exists()) {
        const data = tourneySnap.data();
        if (data.ownerId && data.ownerId !== currentUser.uid) {
            throw new Error("Only the tournament owner can retract invitations.");
        }
    }
    await updateDoc(tourneyRef, {
        pendingCooperatingArbiters: arrayRemove(emailToRetract)
    });

    // 2. Delete the recipient's pending cooperation_invite notification(s) for this
    //    tournament. Best-effort: step 1 is the authoritative one (they are no
    //    longer invited and the rules refuse their acceptance), so a failure here
    //    must not surface as "retract failed" when the retract has in fact
    //    happened. It used to throw permission-denied on every retract — the
    //    notifications rule only let the RECIPIENT touch these — which left the
    //    invite half-retracted and reported an error to the owner.
    try {
        const q = query(
            collection(db, "notifications"),
            where("recipientEmail", "==", emailToRetract),
            where("tournamentId", "==", tournamentId),
            where("type", "==", "cooperation_invite")
        );
        const snapshot = await getDocs(q);
        await Promise.all(snapshot.docs.map(document => deleteDoc(doc(db, "notifications", document.id))));
    } catch (e) {
        console.warn("Retract: could not clear the invitee's notification (the invitation itself was retracted):", e);
    }

    return emailToRetract;
}

// Remove an already-accepted cooperating arbiter from a tournament (owner only).
async function removeCooperatingArbiter(tournamentId, email) {
    if (!currentUser) throw new Error("Must be logged in to remove arbiters.");
    const emailToRemove = (email || '').trim().toLowerCase();
    if (!emailToRemove) throw new Error("No email provided to remove.");

    const tourneyRef = doc(db, "tournaments", tournamentId);
    const tourneySnap = await getDoc(tourneyRef);
    if (!tourneySnap.exists()) throw new Error("Tournament not found.");
    const data = tourneySnap.data();
    if (data.ownerId && data.ownerId !== currentUser.uid) {
        throw new Error("Only the tournament owner can remove cooperating arbiters.");
    }

    // Remove from both lists (also clears any stale pending entry for the same email).
    await updateDoc(tourneyRef, {
        cooperatingArbiters: arrayRemove(emailToRemove),
        pendingCooperatingArbiters: arrayRemove(emailToRemove)
    });

    // Let the removed arbiter know.
    const notiId = Date.now().toString() + "_" + Math.random().toString(36).substring(2, 8);
    await setDoc(doc(db, "notifications", notiId), {
        recipientEmail: emailToRemove,
        senderUid: currentUser.uid,
        senderEmail: currentUser.email,
        senderName: currentUser.displayName || currentUser.email.split('@')[0],
        tournamentId: tournamentId,
        tournamentName: data.name || '',
        type: 'cooperation_removed',
        status: 'pending',
        timestamp: new Date().toISOString()
    });

    return emailToRemove;
}

// Delete a tournament (owner only): notify all cooperating/pending arbiters first, then remove
// the document so it disappears for everyone.
// Delete every Storage object uploaded for a tournament's welcome page. These
// live under welcome-images/{tournamentId}/ (see uploadWelcomeImage); nothing
// else cleaned them up, so before this they accumulated in the bucket forever —
// the one place tournament data was NOT reclaimed on delete. Best-effort and
// isolated: a Storage failure must never block the Firestore deletion. Requires
// the storage.rules `allow delete` on welcome-images (a delete carries no
// request.resource, so it can't pass the image-content-type write guard).
async function deleteWelcomeImagesForTournament(tournamentId) {
    try {
        const dirRef = storageRef(storage, `welcome-images/${tournamentId}`);
        const listing = await listAll(dirRef);
        await Promise.all(listing.items.map(itemRef =>
            deleteObject(itemRef).catch(e => console.warn('Could not delete welcome image:', itemRef.fullPath, e))
        ));
    } catch (e) {
        // listAll on an empty/absent prefix resolves with no items, so a throw
        // here means a real error (permissions, network) — log and move on.
        console.error("Error deleting tournament welcome images from Storage:", e);
    }
}

const DELETE_NEEDS_CONNECTION = 'Deleting a tournament needs an internet connection.';

async function deleteTournamentAndNotify(tournamentId) {
    if (!currentUser) throw new Error("Must be logged in to delete tournaments.");
    const tourneyRef = doc(db, "tournaments", tournamentId);
    const snap = await getDoc(tourneyRef);
    // Deleting needs the server: everything below works from listings of what
    // exists, and offline those listings name only what this device cached. A
    // cache answer here — including a cached "no such document" — stops it before
    // anything is touched (see offlineError).
    if (snap.metadata.fromCache) throw offlineError(DELETE_NEEDS_CONNECTION);
    if (!snap.exists()) return; // already gone

    const data = snap.data();
    if (data.ownerId && data.ownerId !== currentUser.uid) {
        throw new Error("Only the tournament owner can delete this tournament.");
    }

    const recipients = [...new Set(
        [...(data.cooperatingArbiters || []), ...(data.pendingCooperatingArbiters || [])]
            .map(e => (e || '').trim().toLowerCase())
            .filter(Boolean)
    )];

    // Notification ids are built synchronously up front (Date.now() + random
    // suffix keeps them unique even within the same millisecond), then all
    // recipient writes go out in parallel — one round-trip instead of N.
    await Promise.all(recipients.map(email => {
        const notiId = Date.now().toString() + "_" + Math.random().toString(36).substring(2, 8);
        return setDoc(doc(db, "notifications", notiId), {
            recipientEmail: email,
            senderUid: currentUser.uid,
            senderEmail: currentUser.email,
            senderName: currentUser.displayName || currentUser.email.split('@')[0],
            tournamentId: tournamentId,
            tournamentName: data.name || '',
            type: 'tournament_deleted',
            status: 'pending',
            timestamp: new Date().toISOString()
        });
    }));

    // Delete the satellite docs BEFORE the parent meta doc. The welcomePages and
    // scanSessions rules resolve visibility via get() on the parent tournament,
    // so once the parent is gone an orphaned welcome page is both unreadable AND
    // undeletable forever — it must go while the owner still passes those rules.
    // Each cleanup is isolated so one failure can't block the meta-doc deletion.
    try {
        await deleteDoc(doc(db, "welcomePages", tournamentId)); // no-op if it doesn't exist
    } catch (e) {
        console.error("Error deleting tournament welcome page:", e);
    }
    // The welcome page's images in Storage go with it. Done BEFORE the meta doc is
    // deleted: storage.rules lets only the tournament's own arbiters delete them,
    // and checks that against this very document (firestore.get).
    await deleteWelcomeImagesForTournament(tournamentId);
    // Scan sessions are addressed by their token and never queried. The token is a
    // capability, so the rules deliberately grant no `list` on the collection —
    // which means a where('tournamentId') query here was denied on EVERY delete,
    // and the catch turned that into a silent leak: the sessions outlived the
    // tournament, and `allow get: if true` kept the pairings in them readable to
    // anyone still holding the QR, forever.
    //
    // The tokens are already in the meta doc fetched above: on each group for a
    // multi-group tournament, on the root for a single-group one (writeTournamentDoc
    // strips only players/rounds off a group, so scanToken rides along into meta).
    // Deleted one by one, each isolated, so one failure cannot block the others or
    // the meta-doc deletion below.
    const scanTokens = [
        ...(Array.isArray(data.groups) ? data.groups.map(g => g && g.scanToken) : []),
        data.scanToken
    ].filter(Boolean);
    await Promise.all(scanTokens.map(async (scanToken) => {
        try {
            await deleteDoc(doc(db, "scanSessions", scanToken));
        } catch (e) {
            console.error("Error deleting scan session:", scanToken, e);
        }
    }));
    try {
        const regSnap = await getDocs(query(collection(db, "registrations"), where("tournamentId", "==", tournamentId)));
        if (regSnap.metadata.fromCache) throw offlineError(DELETE_NEEDS_CONNECTION);
        await Promise.all(regSnap.docs.map(d => deleteDoc(d.ref)));
    } catch (e) {
        // The connection went mid-delete: stop before the tournament itself goes,
        // so a second attempt can still find what is left and finish it.
        if (e && e.code === 'offline') throw e;
        console.error("Error deleting tournament registrations:", e);
    }

    // Delete the content subcollection first (deleting the parent doc would
    // otherwise orphan them — subcollections are not cascade-deleted), then the
    // meta doc last so listeners see the deletion only once everything is gone.
    // The content deletes have no ordering constraint among themselves, so they
    // run in parallel.
    // The private halves of the rosters go the same way — they are the part of a
    // tournament that most needs not to outlive it.
    for (const sub of ["content", "private"]) {
        try {
            const subSnap = await getDocs(collection(db, "tournaments", tournamentId, sub));
            if (subSnap.metadata.fromCache) throw offlineError(DELETE_NEEDS_CONNECTION);
            await Promise.all(subSnap.docs.map(d => deleteDoc(d.ref)));
        } catch (e) {
            if (e && e.code === 'offline') throw e;
            console.error(`Error deleting tournament ${sub} docs:`, e);
        }
    }

    await deleteDoc(tourneyRef);
    // The doc is gone — drop every cached trace of it so a later save (e.g. of a
    // re-imported tournament with the same id) doesn't trust exists=true, skip a
    // content write as "unchanged", or reuse a stale round index. The caches
    // belong to cloud-sync.js, which does the forgetting on our behalf.
    forgetTournamentCaches(tournamentId);
}

function listenToActiveTournament(tournamentId, callback) {
    // Schema v2: the tournament is meta doc + content subcollection. Listen to
    // both and emit the ASSEMBLED legacy shape, so the apply code in app.js is
    // unchanged. For v1 docs the meta listener alone carries everything and the
    // (empty) content listener never contributes.
    //
    // Only snapshots that come from the server are passed on (snapshotIsAuthoritative).
    // With the persistent cache, losing the connection makes both listeners answer
    // from the cache: a content cache that never held the players reads as "no
    // players", and a meta document the cache never held reads as "deleted" — which
    // closed the tournament on screen. Neither says anything about the cloud, and
    // our own pending writes echoing back say nothing new either.
    // includeMetadataChanges is what delivers the server's confirmation of data the
    // cache already showed, so the first real snapshot is not missed.
    let latestMeta;            // undefined = not yet received, null = deleted
    let metaReady = false;
    let contentReady = false;
    let contentMap = {};
    // The private halves of the rosters (e-mail, phone, registration ids — see
    // js/utils/player-privacy.js). Settled once the first snapshot arrives or the
    // listener is refused; until then the assembled roster would lack them, and an
    // arbiter's app applying it would briefly show empty contact details.
    let privateReady = false;
    let privateMap = {};

    const emit = () => {
        if (!metaReady) return;
        if (latestMeta === null) { callback(null); return; } // remote deletion
        if ((latestMeta.schemaVersion || 1) >= 2) {
            if (!contentReady || !privateReady) return; // wait for the first snapshots
            callback(assembleTournament(latestMeta, contentMap, privateMap));
        } else {
            callback(latestMeta);
        }
    };

    const unsubMeta = onSnapshot(doc(db, "tournaments", tournamentId), { includeMetadataChanges: true }, (snapshot) => {
        if (!snapshotIsAuthoritative(snapshot.metadata)) return;
        metaReady = true;
        latestMeta = snapshot.exists() ? snapshot.data() : null;
        emit();
    }, (error) => {
        console.warn("Active tournament listener error:", error);
    });

    const unsubContent = onSnapshot(collection(db, "tournaments", tournamentId, "content"), { includeMetadataChanges: true }, (qs) => {
        if (!snapshotIsAuthoritative(qs.metadata)) return;
        contentReady = true;
        contentMap = {};
        qs.forEach(d => { contentMap[d.id] = d.data(); });
        emit();
    }, (error) => {
        console.warn("Tournament content listener error:", error);
    });

    const unsubPrivate = onSnapshot(collection(db, "tournaments", tournamentId, "private"), { includeMetadataChanges: true }, (qs) => {
        if (!snapshotIsAuthoritative(qs.metadata)) return;
        privateReady = true;
        privateMap = {};
        qs.forEach(d => { privateMap[d.id] = d.data(); });
        // A server snapshot is a read of every private roster: from here on this
        // device may write them outright (see writePrivatePlayers in cloud-sync.js).
        if (latestMeta) notePrivateHeld(tournamentId, latestMeta);
        emit();
    }, (error) => {
        // Refused (not an arbiter of this tournament after all) or failed: carry on
        // with the public rosters rather than holding the whole tournament back.
        console.warn("Private roster listener error:", error);
        privateReady = true;
        emit();
    });

    // A snapshot that arrived while this device was saving was held back by the
    // handler in js/app.js (hasUnsavedLocalChanges); hand on the latest cloud copy
    // once the save has landed (fired by runScheduledSave in cloud-sync.js).
    window.addEventListener('zw:cloud-save-settled', emit);

    return () => {
        window.removeEventListener('zw:cloud-save-settled', emit);
        unsubMeta(); unsubContent(); unsubPrivate();
    };
}

async function verifyArbiterEmail(email) {
    if (!currentUser) throw new Error("Must be logged in to verify arbiters.");
    const emailToVerify = email.trim().toLowerCase();
    if (emailToVerify === currentUser.email.toLowerCase()) {
        throw new Error("You cannot invite yourself to cooperate on your own tournament!");
    }

    // Case-insensitive verification (server-side lookup; emails stored lowercased)
    const lookup = await lookupArbiterByEmail(emailToVerify);
    if (!lookup.exists) {
        throw new Error("No registered account found with this email address.");
    }

    if (lookup.role === 'player') {
        throw new Error("This email is registered with a Player account. You can only invite users with an Arbiter account.");
    }

    return emailToVerify;
}

async function sendInvitesOnCreation(tournamentId, tournamentName, emails) {
    if (!currentUser || !emails || emails.length === 0) return;
    
    const tourneyRef = doc(db, "tournaments", tournamentId);
    const emailsList = emails.map(e => e.trim().toLowerCase());
    
    await updateDoc(tourneyRef, {
        pendingCooperatingArbiters: arrayUnion(...emailsList)
    });

    // Ids are generated synchronously per recipient (the random suffixes keep
    // them unique within the same millisecond); the writes run in parallel.
    await Promise.all(emailsList.map(emailToInvite => {
        const notiId = Date.now().toString() + "_" + Math.random().toString(36).substring(2, 7) + "_" + Math.random().toString(36).substring(2, 5);
        return setDoc(doc(db, "notifications", notiId), {
            recipientEmail: emailToInvite,
            senderUid: currentUser.uid,
            senderEmail: currentUser.email,
            senderName: currentUser.displayName || currentUser.email.split('@')[0],
            tournamentId: tournamentId,
            tournamentName: tournamentName,
            type: 'cooperation_invite',
            status: 'pending',
            timestamp: new Date().toISOString()
        });
    }));
}

export {
    lookupArbiterByEmail,
    inviteArbiterByEmail,
    listenToNotifications,
    acceptCooperationInvite,
    declineCooperationInvite,
    deleteNotification,
    retractCooperationInvite,
    removeCooperatingArbiter,
    deleteTournamentAndNotify,
    listenToActiveTournament,
    verifyArbiterEmail,
    sendInvitesOnCreation
};
